// Mock data for the Pulse scaffold. Shaped to the extended pulse_ schema so the
// later backend wiring maps 1:1. Swap the service methods to Supabase reads and
// these shapes stay the same. Kept in one file (like swell/_mock.js).

// -- palette + helpers ------------------------------------------------
export const AVATARS = ["#6366f1", "#0b8043", "#b45309", "#9333ea", "#1f9d63", "#db2777", "#0891b2", "#e11d48"];
export const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
export const colorFor = (name) => {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATARS[h % AVATARS.length];
};

// -- team (channel_account owners) ------------------------------------
export const TEAM = [
  { id: "u1", name: "Ahmer Afzal", role: "Founder", initials: "AA", color: "#6366f1", status: "available", ai: false },
  { id: "u2", name: "Lena Haddad", role: "Partnerships Lead", initials: "LH", color: "#0b8043", status: "available", ai: false },
  { id: "u3", name: "Marco Rossi", role: "Account Manager", initials: "MR", color: "#b45309", status: "busy", note: "On a call", ai: false },
  { id: "u4", name: "Priya Nair", role: "Creator Manager", initials: "PN", color: "#9333ea", status: "leave", note: "PTO · back Thu", ai: true },
  { id: "u5", name: "Sara Khan", role: "Coordinator", initials: "SK", color: "#1f9d63", status: "available", ai: false },
];

// -- campaigns --------------------------------------------------------
export const CAMPAIGNS = [
  { id: 101, label: "Huda Beauty — SS26", color: "#db2777" },
  { id: 102, label: "L'Oréal Ramadan '26", color: "#b45309" },
  { id: 103, label: "Nike MENA — Always On", color: "#1f2430" },
  { id: 104, label: "Chalhoub Group — Q3", color: "#9333ea" },
  { id: 105, label: "Nestlé — Nido Launch", color: "#0891b2" },
];

// -- Overview ---------------------------------------------------------
export const OVERVIEW = {
  kpis: [
    { key: "unread", num: "38", label: "Unread across channels", icon: "pi-inbox", tone: "indigo" },
    { key: "waiting", num: "12", label: "Awaiting first reply", icon: "pi-clock", tone: "amber" },
    { key: "sla", num: "94%", label: "Within SLA today", icon: "pi-check-circle", tone: "green" },
    { key: "calls", num: "7", label: "Calls logged today", icon: "pi-phone", tone: "purple" },
  ],
  activity: [
    { initials: "HB", name: "Huda — Nadia B.", time: "2m", channel: "whatsapp", preview: "Sounds great, sending the brief over now 🙌", campaign: "Huda Beauty — SS26" },
    { initials: "LO", name: "L'Oréal — Karim", time: "18m", channel: "email", preview: "Re: Ramadan creator shortlist — approved 3 of 5", campaign: "L'Oréal Ramadan '26" },
    { initials: "NK", name: "Nike — Dana", time: "44m", channel: "call", preview: "Call · 12:04 — discussed Q3 always-on budget", campaign: "Nike MENA — Always On" },
    { initials: "CH", name: "Chalhoub — Rania", time: "1h", channel: "email", preview: "Can we move the sign-off call to Thursday?", campaign: "Chalhoub Group — Q3" },
    { initials: "NE", name: "Nestlé — Omar", time: "2h", channel: "whatsapp", preview: "Received, thank you! Reviewing internally.", campaign: "Nestlé — Nido Launch" },
  ],
  today: [
    { time: "10:30", title: "Huda SS26 kickoff", sub: "Meet · with Nadia B." },
    { time: "13:00", title: "Nike Q3 budget call", sub: "Call · Dana Al-Rashid" },
    { time: "16:00", title: "Creator sign-off", sub: "Meeting · internal" },
  ],
  sentiment: { pos: 61, neu: 28, risk: 11 },
  needsAttention: [
    { name: "Chalhoub — Rania", reason: "Waiting 6h · SLA breached", channel: "email", sentiment: "at_risk" },
    { name: "Adidas — new enquiry", reason: "Unassigned · 3h", channel: "whatsapp", sentiment: "neutral" },
    { name: "Nestlé — Omar", reason: "Negative sentiment detected", channel: "email", sentiment: "negative" },
  ],
  byCampaign: [
    { name: "Huda Beauty — SS26", email: 14, wa: 22, calls: 3, total: 39 },
    { name: "L'Oréal Ramadan '26", email: 20, wa: 9, calls: 5, total: 34 },
    { name: "Nike MENA — Always On", email: 8, wa: 11, calls: 6, total: 25 },
    { name: "Chalhoub Group — Q3", email: 12, wa: 4, calls: 2, total: 18 },
    { name: "Nestlé — Nido Launch", email: 6, wa: 7, calls: 1, total: 14 },
  ],
};

export const CHANNEL_META = {
  email:    { label: "Email", icon: "pi-envelope", color: "#4f6bed" },
  whatsapp: { label: "WhatsApp", icon: "pi-whatsapp", color: "#1f9d63" },
  call:     { label: "Call", icon: "pi-phone", color: "#7c3aed" },
  calendar: { label: "Calendar", icon: "pi-calendar", color: "#1a73e8" },
};

// =====================================================================
// ADMIN
// =====================================================================

// The four gated features and how each is labelled in the Access matrix.
export const ACCESS_FEATURES = [
  { key: "email", label: "Email", icon: "pi-envelope" },
  { key: "phone", label: "Phone", icon: "pi-phone" },
  { key: "whatsapp", label: "WhatsApp", icon: "pi-whatsapp" },
  { key: "calendar", label: "Calendar", icon: "pi-calendar" },
];

// Access levels, low → high. "team" = read others; "all" = manage everyone's.
export const ACCESS_LEVELS = [
  { key: "none", label: "No access", short: "Off", cls: "muted", desc: "Cannot open this channel." },
  { key: "own",  label: "Own only",  short: "Own", cls: "indigo", desc: "Only their own conversations." },
  { key: "team", label: "View team", short: "Team", cls: "amber", desc: "Read teammates' conversations." },
  { key: "all",  label: "Full access", short: "Full", cls: "green", desc: "View & manage everyone's." },
];

// Staff members granted Pulse access (the Access roster). `admin` = can manage
// this Admin area (access + settings). Higher management typically gets team/all.
export const PULSE_MEMBERS = [
  { id: "u1", name: "Ahmer Afzal", role: "Founder", initials: "AA", color: "#6366f1", admin: true,
    access: { email: "all", phone: "all", whatsapp: "all", calendar: "all" } },
  { id: "u2", name: "Lena Haddad", role: "Partnerships Lead", initials: "LH", color: "#0b8043", admin: true,
    access: { email: "team", phone: "team", whatsapp: "team", calendar: "team" } },
  { id: "u3", name: "Marco Rossi", role: "Account Manager", initials: "MR", color: "#b45309", admin: false,
    access: { email: "own", phone: "own", whatsapp: "own", calendar: "own" } },
  { id: "u4", name: "Priya Nair", role: "Creator Manager", initials: "PN", color: "#9333ea", admin: false,
    access: { email: "own", phone: "none", whatsapp: "own", calendar: "own" } },
  { id: "u5", name: "Sara Khan", role: "Coordinator", initials: "SK", color: "#1f9d63", admin: false,
    access: { email: "own", phone: "none", whatsapp: "own", calendar: "own" } },
];

// Staff who exist in the main app but aren't yet in Pulse (the "add member" pool).
export const STAFF_DIRECTORY = [
  { id: "u6", name: "Yusuf Demir", role: "Account Manager", initials: "YD", color: "#0891b2" },
  { id: "u7", name: "Aisha Malik", role: "Creator Manager", initials: "AM", color: "#db2777" },
  { id: "u8", name: "Tom Becker", role: "Finance", initials: "TB", color: "#e11d48" },
];

// Connected mailboxes, grouped by owning organisation. Email spans multiple
// domains: brandripplr.com, starsmagnet.com, plus connected influencer
// inboxes the Stars Magnet team operates. `assigned` = member ids with access.
export const MAILBOX_GROUPS = [
  {
    org: "Brandripplr", domain: "brandripplr.com", note: "Company domain — team + shared inboxes.",
    accounts: [
      { id: "mb1", address: "ahmer@brandripplr.com", label: "Ahmer Afzal", kind: "personal", status: "connected", assigned: ["u1"], radar: false },
      { id: "mb2", address: "partnerships@brandripplr.com", label: "Partnerships", kind: "shared", status: "connected", assigned: ["u2", "u3"], radar: true },
      { id: "mb3", address: "info@brandripplr.com", label: "General enquiries", kind: "shared", status: "connected", assigned: ["u5"], radar: false },
    ],
  },
  {
    org: "Stars Magnet", domain: "starsmagnet.com", note: "Talent-agency domain — the Stars Magnet team.",
    accounts: [
      { id: "mb4", address: "lena@starsmagnetmanagement.com", label: "Lena Haddad", kind: "personal", status: "connected", assigned: ["u2"], radar: true },
      { id: "mb5", address: "talent@starsmagnetmanagement.com", label: "Talent desk", kind: "shared", status: "connected", assigned: ["u4", "u5"], radar: false },
    ],
  },
  {
    org: "Influencer inboxes", domain: "connected creator accounts", note: "Creator mailboxes the Stars Magnet team manages on their behalf.",
    accounts: [
      { id: "mb6", address: "hello@nadiabeauty.com", label: "Nadia B. (creator)", kind: "influencer", status: "connected", assigned: ["u4"], radar: false },
      { id: "mb7", address: "omar.creates@gmail.com", label: "Omar K. (creator)", kind: "influencer", status: "degraded", assigned: ["u4"], radar: false },
    ],
  },
];

// Per-member email signatures (appended per sender on outgoing mail).
export const SIGNATURES = [
  { userId: "u1", name: "Ahmer Afzal", role: "Founder", phone: "+971 50 000 0001", website: "brandripplr.com", address: "Dubai, UAE" },
  { userId: "u2", name: "Lena Haddad", role: "Partnerships Lead", phone: "+971 50 000 0002", website: "starsmagnet.com", address: "Dubai, UAE" },
  { userId: "u3", name: "Marco Rossi", role: "Account Manager", phone: "+971 50 000 0003", website: "brandripplr.com", address: "Dubai, UAE" },
];

export const MAILBOX_KIND = {
  personal:   { label: "Personal", cls: "indigo" },
  shared:     { label: "Shared / role", cls: "amber" },
  influencer: { label: "Influencer", cls: "green" },
};

// =====================================================================
// EMAIL
// =====================================================================
export const MAIL_FOLDERS = [
  { key: "inbox", label: "Inbox", icon: "pi-inbox", count: 18 },
  { key: "flagged", label: "Flagged", icon: "pi-flag", count: 3 },
  { key: "sent", label: "Sent", icon: "pi-send", count: 0 },
  { key: "drafts", label: "Drafts", icon: "pi-file-edit", count: 2 },
  { key: "archive", label: "Archive", icon: "pi-inbox", count: 0 },
  { key: "spam", label: "Spam", icon: "pi-ban", count: 1 },
];

export const MAIL_LABELS = [
  { key: "Clients", color: "#4f6bed" },
  { key: "Creators", color: "#1f9d63" },
  { key: "Agencies", color: "#9333ea" },
  { key: "Leads", color: "#f5a623" },
];

// One thread = one conversation. `ownerId: null` → unassigned (triggers the
// assign-and-route flow). `campaignId: null` → not auto-linked.
export const EMAIL_THREADS = [
  {
    id: "t1", folder: "inbox", unread: true, flagged: false, phishing: false,
    from: { name: "Adidas Originals MENA", email: "partnerships@adidas-mena.com", initials: "AD" },
    ownerId: null, campaignId: null, spans: [], mailbox: "partnerships@brandripplr.com",
    subject: "Partnership enquiry — Adidas Originals SS26", snippet: "Hi team, we're planning an always-on creator programme across the GCC…",
    date: "9:14 AM", sla: "New · unassigned", slaTone: "amber", sentiment: "neutral", open: null,
    messages: [
      { id: "m1", dir: "in", fromName: "Dana Al-Rashid", fromEmail: "partnerships@adidas-mena.com", initials: "DA", date: "Today 9:14 AM",
        body: "Hi team,\n\nWe're planning an always-on creator programme across the GCC for SS26 and would love to explore working with Brandripplr. Could we set up an intro call next week?\n\nBest,\nDana", attachments: [], tracking: null },
    ],
    notes: [],
  },
  {
    id: "t2", folder: "inbox", unread: true, flagged: true, phishing: false,
    from: { name: "Chalhoub — Rania", email: "rania.k@chalhoub.com", initials: "RK" },
    ownerId: "u2", campaignId: 104, spans: [104], mailbox: "lena@starsmagnet.com",
    subject: "Re: Q3 sign-off — can we move to Thursday?", snippet: "Thanks for the deck. Something's come up on our side…",
    date: "8:02 AM", sla: "SLA breached · 6h", slaTone: "red", sentiment: "at_risk", open: "Opened 1×",
    messages: [
      { id: "m2", dir: "in", fromName: "Rania Khoury", fromEmail: "rania.k@chalhoub.com", initials: "RK", date: "Today 8:02 AM",
        body: "Thanks for the deck. Something's come up on our side — can we move the sign-off call to Thursday afternoon instead of tomorrow?", attachments: [], tracking: null },
    ],
    notes: [
      { ini: "MR", who: "Marco Rossi", when: "7:40 AM", text: "@Lena heads up — Rania sounds a bit tense, worth a quick call rather than email." },
    ],
  },
  {
    id: "t3", folder: "inbox", unread: false, flagged: false, phishing: false,
    from: { name: "L'Oréal — Karim", email: "karim.h@loreal.com", initials: "KH" },
    ownerId: "u3", campaignId: 102, spans: [102], mailbox: "partnerships@brandripplr.com",
    subject: "Ramadan creator shortlist — approved 3 of 5", snippet: "Great picks. We're happy with Nadia, Omar and Layla…",
    date: "Yesterday", sla: "Replied", slaTone: "green", sentiment: "positive", open: "Opened 2×",
    messages: [
      { id: "m3a", dir: "in", fromName: "Karim Hassan", fromEmail: "karim.h@loreal.com", initials: "KH", date: "Yesterday 4:20 PM",
        body: "Great picks. We're happy with Nadia, Omar and Layla — let's proceed with those three. Can you share rates and availability for the other two before we decide?", attachments: [{ name: "Shortlist_feedback.pdf", size: "240 KB" }], tracking: null },
      { id: "m3b", dir: "out", fromName: "Marco Rossi", fromEmail: "partnerships@brandripplr.com", initials: "MR", date: "Yesterday 5:05 PM",
        body: "Perfect, thanks Karim! Locking those three in now. I'll send rates + availability for the remaining two by tomorrow AM.", attachments: [], tracking: { opened: "Opened 2×", device: "iPhone · Dubai" } },
    ],
    notes: [],
  },
  {
    id: "t4", folder: "inbox", unread: false, flagged: false, phishing: false,
    from: { name: "Huda Beauty — Nadia", email: "nadia.b@hudabeauty.com", initials: "NB" },
    ownerId: "u1", campaignId: 101, spans: [101, 103], mailbox: "ahmer@brandripplr.com",
    subject: "SS26 brief & shoot timelines", snippet: "Attaching the brief — shoot window is early March…",
    date: "Mon", sla: "Replied", slaTone: "green", sentiment: "positive", open: "Opened 3×",
    messages: [
      { id: "m4", dir: "in", fromName: "Nadia B.", fromEmail: "nadia.b@hudabeauty.com", initials: "NB", date: "Mon 11:12 AM",
        body: "Hi Ahmer — attaching the SS26 brief. Shoot window is early March, and we'd like to fold in the Nike collab angle too. Timelines inside.", attachments: [{ name: "SS26_Brief_v2.pdf", size: "1.4 MB" }, { name: "Timelines.xlsx", size: "88 KB" }], tracking: null },
    ],
    notes: [],
  },
  {
    id: "t5", folder: "spam", unread: true, flagged: false, phishing: true,
    from: { name: "Account Security", email: "billing@brandriipplr-secure.com", initials: "AS" },
    ownerId: null, campaignId: null, spans: [], mailbox: "info@brandripplr.com",
    subject: "Urgent: verify your account within 24 hours", snippet: "Your account will be suspended unless you confirm…",
    date: "7:30 AM", sla: "Flagged", slaTone: "red", sentiment: "neutral", open: null,
    messages: [
      { id: "m5", dir: "in", fromName: "Account Security", fromEmail: "billing@brandriipplr-secure.com", initials: "AS", date: "Today 7:30 AM",
        body: "Your account will be suspended unless you confirm your details within 24 hours. Click the secure link to verify immediately.", attachments: [], tracking: null },
    ],
    notes: [],
  },
  {
    id: "t6", folder: "inbox", unread: false, flagged: false, phishing: false,
    from: { name: "Nestlé — Omar", email: "omar.f@nestle.com", initials: "OF" },
    ownerId: "u4", campaignId: 105, spans: [105], mailbox: "talent@starsmagnet.com",
    subject: "Re: Nido launch assets — revisions needed", snippet: "The first cut isn't quite hitting the brief…",
    date: "Mon", sla: "Waiting", slaTone: "amber", sentiment: "negative", open: "Opened 1×",
    messages: [
      { id: "m6", dir: "in", fromName: "Omar Farouk", fromEmail: "omar.f@nestle.com", initials: "OF", date: "Mon 9:40 AM",
        body: "The first cut isn't quite hitting the brief — the tone feels off and the logo placement is wrong. We need revisions before EOW or we'll miss the launch window.", attachments: [], tracking: null },
    ],
    notes: [
      { ini: "PN", who: "Priya Nair", when: "Mon 10:15 AM", text: "Briefing the creator on revisions today. Flagging risk to launch date." },
    ],
  },
];

export const SAVED_REPLIES = [
  { name: "Intro — new brand", preview: "Thanks for reaching out! We'd love to explore…", body: "Thanks for reaching out — we'd love to explore working together. Are you free for a quick intro call this week? I can share a short deck on how we run creator programmes in the region." },
  { name: "Awaiting brief", preview: "To get started, could you share the brief…", body: "To get started on our side, could you share the campaign brief, budget range and any must-have deliverables? Once we have those we'll come back with a proposed creator shortlist." },
  { name: "Timeline confirm", preview: "Confirming the timeline below — shout if…", body: "Confirming the timeline below — please shout if anything needs to shift:\n\n• Brief sign-off\n• Creator confirmation\n• Content shoot\n• Review & approvals\n• Go live" },
  { name: "Polite decline", preview: "Thanks so much for thinking of us…", body: "Thanks so much for thinking of us. This one isn't the right fit for our roster at the moment, but we'd love to stay in touch for future campaigns." },
];
